/**
 * Schemas Zod — validacao de fronteira (spec 6.4).
 *
 * Regra: TODO input que cruza a fronteira da API passa por um destes schemas,
 * e todos usam `.strict()` para rejeitar campos desconhecidos. Isso e o que
 * impede um cliente de injetar `owner_id` no corpo da requisicao (spec 6.2).
 */

import { z } from 'zod';
import {
  COUNTDOWN_REPEATS,
  HABIT_GOAL_TYPES,
  POMODORO_MODES,
  SNOOZE_PRESETS,
  TASK_PAGE_SIZE,
  TASK_STATUS,
} from './constants.js';

// ------------------------------------------------------------------ primitivos

const uuid = z
  .string()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
    'id deve ser um UUID',
  );

/** Epoch em ms. Limite superior evita confundir segundos com milissegundos. */
const epochMs = z.number().int().min(0).max(4_102_444_800_000);

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'data deve estar no formato YYYY-MM-DD');

/**
 * Hora de parede 'HH:MM', 00:00 a 23:59. Usada nos lembretes de habito e de
 * contagem, que nao tem instante fixo: o fuso entra so na hora de enviar.
 */
const wallTime = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'horario deve estar no formato HH:MM');

const hexColor = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, 'cor deve ser hexadecimal no formato #RRGGBB');

/**
 * Uniao de literais, e nao `z.number().min(0).max(3)`, para que o tipo
 * INFERIDO seja o `Priority` do dominio (0|1|2|3) e nao um `number` largo.
 * Com o numero largo, cada consumidor precisaria re-estreitar o valor na mao,
 * e um `as Priority` esquecido deixaria passar um 7 ate o CHECK do SQLite.
 */
const priority = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)], {
  errorMap: () => ({ message: 'prioridade deve ser 0 (nenhuma), 1, 2 ou 3 (alta)' }),
});

/** Mesmo motivo: infere o `Quadrant` do dominio (1|2|3|4). */
const quadrant = z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)], {
  errorMap: () => ({ message: 'quadrante deve ser 1, 2, 3 ou 4' }),
});

// ------------------------------------------------------------------ settings

export const userSettingsSchema = z
  .object({
    theme: z.enum(['light', 'dark', 'system']),
    tabs: z.array(z.string().min(1)).min(1).max(5),
    pomodoro: z
      .object({
        focusSeconds: z.number().int().min(60).max(7200),
        shortBreakSeconds: z.number().int().min(60).max(3600),
        longBreakSeconds: z.number().int().min(60).max(3600),
        cyclesBeforeLongBreak: z.number().int().min(1).max(12),
      })
      .strict(),
    manualQuadrantOnly: z.boolean(),
  })
  .strict();

export const updateSettingsSchema = userSettingsSchema.partial();

// --------------------------------------------------------------------- lists

export const createListSchema = z
  .object({
    id: uuid,
    name: z.string().trim().min(1).max(120),
    color: hexColor.default('#2F6FE4'),
    icon: z.string().max(32).nullable().default(null),
    shared: z.boolean().default(false),
    sortOrder: z.number().default(0),
  })
  .strict();

export const updateListSchema = createListSchema
  .omit({ id: true })
  .extend({ archived: z.boolean() })
  .partial()
  .strict();

// ---------------------------------------------------------------------- tags

export const createTagSchema = z
  .object({
    id: uuid,
    name: z.string().trim().min(1).max(60),
    color: hexColor.default('#07B494'),
  })
  .strict();

export const updateTagSchema = createTagSchema.omit({ id: true }).partial().strict();

// --------------------------------------------------------------------- tasks

export const createTaskSchema = z
  .object({
    // O id vem do cliente (UUID v7) — e o que torna POST /tasks idempotente
    // e permite criacao offline.
    id: uuid,
    title: z.string().trim().min(1).max(500),
    notes: z.string().max(20_000).nullable().default(null),
    listId: uuid.nullable().default(null),
    parentId: uuid.nullable().default(null),
    priority: priority.default(0),
    quadrant: quadrant.nullable().default(null),
    quadrantAuto: z.boolean().default(true),
    dueAt: epochMs.nullable().default(null),
    allDay: z.boolean().default(false),
    reminderAt: epochMs.nullable().default(null),
    rrule: z.string().max(300).nullable().default(null),
    estimatePomos: z.number().int().min(0).max(99).default(0),
    sortOrder: z.number().default(0),
    tagIds: z.array(uuid).max(20).default([]),
  })
  .strict();

export const updateTaskSchema = createTaskSchema
  .omit({ id: true })
  .extend({ status: z.enum(TASK_STATUS) })
  .partial()
  .strict();

export const snoozeTaskSchema = z
  .union([
    z.object({ preset: z.enum(SNOOZE_PRESETS) }).strict(),
    z.object({ dueAt: epochMs }).strict(),
  ]);

export const reorderTasksSchema = z
  .object({
    items: z
      .array(z.object({ id: uuid, sortOrder: z.number() }).strict())
      .min(1)
      .max(500),
  })
  .strict();

/**
 * Booleano de query string.
 *
 * `z.coerce.boolean()` NAO serve aqui: ele aplica `Boolean(value)`, e
 * `Boolean('false')` e `true`. Um parametro que significa o oposto do que
 * escreve e uma armadilha — `?includeDone=false` traria as concluidas.
 */
const queryBoolean = z.preprocess((value) => {
  if (typeof value !== 'string') return value;
  if (value === 'false' || value === '0' || value === '') return false;
  if (value === 'true' || value === '1') return true;
  return value;
}, z.boolean());

export const taskQuerySchema = z
  .object({
    view: z.enum(['today', 'matrix', 'list', 'upcoming', 'trash']).default('list'),
    listId: uuid.optional(),
    /**
     * Filtro por tag. Uma so, e nao um array: filtrar por varias exigiria
     * escolher entre E e OU, e nenhuma das duas semanticas e obvia para quem
     * clicou num chip. Uma tag por vez responde a pergunta que a tela faz.
     */
    tagId: uuid.optional(),
    /**
     * Filtro por tarefa-pai, usado pela secao de subtarefas do painel de
     * detalhe. Aceita a string `null` para pedir so as tarefas de primeiro
     * nivel — sem isso nao haveria como distinguir "sem filtro" de "sem pai",
     * porque um parametro ausente ja significa a primeira coisa.
     */
    parentId: z.union([uuid, z.literal('null')]).optional(),
    since: z.coerce.number().int().min(0).optional(),
    includeDone: queryBoolean.default(false),
    /**
     * Busca textual (spec 10.2). Casa em titulo e notas.
     *
     * O teto de 200 nao e cerimonia: sem ele, uma query de 1 MB viraria um LIKE
     * sobre a base inteira a cada tecla digitada.
     */
    q: z.string().trim().min(1).max(200).optional(),
    // Janela de vencimento, usada pelo calendario (spec 9.4). Sem ela, montar
    // uma semana exigiria baixar a base inteira e filtrar no cliente — o que
    // funciona com 150 tarefas e deixa de funcionar em silencio depois.
    from: z.coerce.number().int().min(0).max(4_102_444_800_000).optional(),
    to: z.coerce.number().int().min(0).max(4_102_444_800_000).optional(),
    /**
     * Teto de linhas na resposta. Tem PADRAO, e nao e opcional: um cliente que
     * esquece o parametro precisa receber o teto, senao a protecao vale so
     * para quem lembrou dela.
     *
     * Quem quer o bloco seguinte pede um `limit` maior — a resposta traz
     * `hasMore` para a tela saber que existe algo alem. Nao ha `offset`: a
     * ordenacao e por prazo e muda quando uma tarefa e adiada, entao uma
     * segunda pagina por deslocamento pularia ou repetiria linhas.
     */
    limit: z.coerce.number().int().min(1).max(2000).default(TASK_PAGE_SIZE),
    /**
     * Recorte das CONCLUIDAS, em dias civis do usuario. So tem efeito junto com
     * `includeDone`, e so quando pedido: a busca e o calendario precisam
     * enxergar o que foi concluido em qualquer epoca.
     *
     * Tarefa concluida sem carimbo (`completed_at IS NULL`, possivel em
     * registros antigos) NAO e cortada — some-la seria esconder trabalho por
     * causa de um dado ausente.
     */
    doneDays: z.coerce.number().int().min(1).max(3650).optional(),
  })
  .strict();

// ------------------------------------------------------------------- habitos

export const createHabitSchema = z
  .object({
    id: uuid,
    name: z.string().trim().min(1).max(120),
    icon: z.string().max(32).nullable().default(null),
    color: hexColor.default('#07B494'),
    goalType: z.enum(HABIT_GOAL_TYPES).default('boolean'),
    goalTarget: z.number().positive().max(10_000).default(1),
    unit: z.string().max(20).nullable().default(null),
    daysMask: z.number().int().min(0).max(127).default(127),
    reminderTime: wallTime.nullable().default(null),
  })
  .strict();

export const updateHabitSchema = createHabitSchema
  .omit({ id: true })
  .extend({ archived: z.boolean() })
  .partial()
  .strict();

export const logHabitSchema = z
  .object({
    date: isoDate,
    value: z.number().min(0).max(10_000).default(1),
    note: z.string().max(500).nullable().default(null),
  })
  .strict();

/**
 * Janela de `GET /habits/stats`, em data PURA e nao em epoch.
 *
 * O periodo de um habito e um intervalo de dias civis do usuario, nao de
 * instantes: aceitar epoch aqui obrigaria o cliente a resolver o fuso antes de
 * perguntar, que e justamente o que o servidor sabe fazer melhor.
 */
export const habitStatsQuerySchema = z
  .object({
    from: isoDate.optional(),
    to: isoDate.optional(),
  })
  .strict();

// ------------------------------------------------------------------ pomodoro

export const startPomodoroSchema = z
  .object({
    id: uuid,
    taskId: uuid.nullable().default(null),
    mode: z.enum(POMODORO_MODES),
    startedAt: epochMs,
    plannedS: z.number().int().min(0).max(24 * 3600).default(1500),
  })
  .strict();

export const endPomodoroSchema = z
  .object({
    endedAt: epochMs,
    /**
     * Tempo efetivamente decorrido, informado pelo cliente. O servidor o
     * recalcula a partir de `started_at` e usa o MENOR dos dois — o cliente
     * pode ter ficado suspenso, mas nao pode inflar o proprio numero.
     */
    durationS: z.number().int().min(0).max(24 * 3600),
    completed: z.boolean(),
    /**
     * Anotacao do que aconteceu neste bloco. Pertence a SESSAO e nao a tarefa:
     * gravar em `tasks.notes` sobrescreveria o campo a cada ciclo e apagaria o
     * historico, que e o unico motivo de anotar.
     */
    note: z.string().max(2000).nullable().default(null),
  })
  .strict();

export const pomodoroStatsQuerySchema = z
  .object({
    range: z.enum(['week', 'month']).default('week'),
  })
  .strict();

// ---------------------------------------------------------------- countdowns

export const createCountdownSchema = z
  .object({
    id: uuid,
    title: z.string().trim().min(1).max(120),
    icon: z.string().max(32).nullable().default(null),
    targetDate: isoDate,
    repeat: z.enum(COUNTDOWN_REPEATS).default('none'),
    color: hexColor.default('#1B2C56'),
    reminderTime: wallTime.nullable().default(null),
    // Um ano de antecedencia cobre a contagem anual; alem disso o aviso
    // chegaria antes da ocorrencia anterior.
    reminderDaysBefore: z.number().int().min(0).max(365).default(0),
  })
  .strict();

export const updateCountdownSchema = createCountdownSchema
  .omit({ id: true })
  .partial()
  .strict();

// ---------------------------------------------------------------------- push

export const pushSubscribeSchema = z
  .object({
    id: uuid,
    endpoint: z.string().url().max(2000),
    p256dh: z.string().min(1).max(200),
    auth: z.string().min(1).max(200),
    userAgent: z.string().max(300).nullable().default(null),
  })
  .strict();

/**
 * Cancelamento. Identificado pelo ENDPOINT, nao pelo id da linha: quem pede o
 * cancelamento e o navegador, e o que ele tem em maos e o proprio endpoint
 * devolvido por `pushManager.getSubscription()`. O id foi gerado pelo cliente
 * na inscricao e pode ter se perdido junto com o `localStorage`.
 */
export const pushUnsubscribeSchema = z
  .object({
    endpoint: z.string().url().max(2000),
  })
  .strict();

// ---------------------------------------------------------------------- sync

export const syncOpSchema = z
  .object({
    opId: uuid,
    entity: z.enum(['task', 'list', 'tag', 'habit', 'habit_log', 'countdown']),
    action: z.enum(['create', 'update', 'delete', 'complete', 'restore']),
    entityId: uuid,
    /** Payload ja validado pelo schema da entidade no handler. */
    payload: z.unknown(),
    /** Momento em que a operacao foi enfileirada no cliente. */
    queuedAt: epochMs,
  })
  .strict();

export const syncRequestSchema = z
  .object({
    since: epochMs,
    ops: z.array(syncOpSchema).max(500).default([]),
  })
  .strict();

// ------------------------------------------------------------------ inferidos

/**
 * Dois tipos por schema, e a distincao nao e cosmetica:
 *
 *  - `z.infer` e a SAIDA — o que o servidor tem em maos depois do parse, com
 *    todos os defaults ja aplicados. E o tipo certo para os handlers.
 *  - `z.input` e a ENTRADA — o que o cliente precisa montar, onde tudo que tem
 *    `.default()` e opcional.
 *
 * Sem os tipos de entrada, criar uma tarefa no front exigiria preencher os
 * quinze campos na mao so para satisfazer o compilador, e a fonte unica de
 * verdade da spec 12 viraria um estorvo em vez de uma garantia.
 */
export type StartPomodoroPayload = z.input<typeof startPomodoroSchema>;
export type EndPomodoroPayload = z.input<typeof endPomodoroSchema>;
export type CreateTaskPayload = z.input<typeof createTaskSchema>;
export type UpdateTaskPayload = z.input<typeof updateTaskSchema>;
export type CreateListPayload = z.input<typeof createListSchema>;
export type UpdateListPayload = z.input<typeof updateListSchema>;
export type CreateTagPayload = z.input<typeof createTagSchema>;
export type UpdateTagPayload = z.input<typeof updateTagSchema>;
export type CreateHabitPayload = z.input<typeof createHabitSchema>;
export type UpdateHabitPayload = z.input<typeof updateHabitSchema>;
export type LogHabitPayload = z.input<typeof logHabitSchema>;
export type CreateCountdownPayload = z.input<typeof createCountdownSchema>;
export type UpdateCountdownPayload = z.input<typeof updateCountdownSchema>;

export type UserSettingsInput = z.infer<typeof userSettingsSchema>;
export type CreateTaskInput = z.infer<typeof createTaskSchema>;
export type UpdateTaskInput = z.infer<typeof updateTaskSchema>;
export type SnoozeTaskInput = z.infer<typeof snoozeTaskSchema>;
export type TaskQueryInput = z.infer<typeof taskQuerySchema>;
export type CreateListInput = z.infer<typeof createListSchema>;
export type UpdateListInput = z.infer<typeof updateListSchema>;
export type CreateHabitInput = z.infer<typeof createHabitSchema>;
export type LogHabitInput = z.infer<typeof logHabitSchema>;
export type CreateCountdownInput = z.infer<typeof createCountdownSchema>;
export type SyncRequestInput = z.infer<typeof syncRequestSchema>;
export type SyncOpInput = z.infer<typeof syncOpSchema>;
